/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import useProfile from "../hooks/useProfile";
import {
  collection,
  query,
  where,
  onSnapshot,
  getDocs,
  limit,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { citiesList, getDistrictsForCity } from "../components/citiesData";
import "../styles/ProfilePage.css";

function ProfilePage() {
  const API_BASE = "https://api-qf4yqlwrnq-uc.a.run.app";
  const { currentUser } = useAuth();

  const {
    profile,
    referralLinks,
    userActivities,
    loading,
    error,
    updateProfile,
  } = useProfile(currentUser);

  const [dynamicCounts, setDynamicCounts] = useState({});
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [showCopyToast, setShowCopyToast] = useState(false);

  function updateCount(activityKey, newCount) {
    setDynamicCounts((prev) => ({ ...prev, [activityKey]: newCount }));
  }

  const [isEditing, setIsEditing] = useState(false);
  const [districts, setDistricts] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    phoneNumber: "",
    city: "",
    district: "",
  });

  const handleUnsubscribe = async (activityId) => {
    try {
      const response = await fetch(
        `${API_BASE}/api/user-activities/unsubscribe`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: currentUser.uid, activityId }),
        },
      );
      if (response.ok) window.location.reload();
      else alert("حدث خطأ أثناء إلغاء الاشتراك!");
    } catch {
      alert("تعذر إلغاء الاشتراك!");
    }
  };

  const handleResubscribe = async (activityId) => {
    try {
      const response = await fetch(
        `${API_BASE}/api/user-activities/resubscribe`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: currentUser.uid, activityId }),
        },
      );
      if (response.ok) window.location.reload();
      else alert("حدث خطأ أثناء إعادة الاشتراك!");
    } catch {
      alert("تعذر إعادة الاشتراك!");
    }
  };

  const handleEdit = () => {
    if (profile) {
      const cityVal = profile.city || "";
      setDistricts(cityVal ? getDistrictsForCity(cityVal) : []);
      setFormData({
        name: profile.name || "",
        phoneNumber: profile.phoneNumber || "",
        city: cityVal,
        district: profile.district || "",
      });
      setIsEditing(true);
    }
  };

  const handleCityChange = (e) => {
    const cityName = e.target.value;
    setFormData((prev) => ({ ...prev, city: cityName, district: "" }));
    setDistricts(cityName ? getDistrictsForCity(cityName) : []);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const success = await updateProfile(formData);
    if (success) setIsEditing(false);
  };

  const handleCopyReferralCode = () => {
    if (profile?.referralCode) {
      navigator.clipboard.writeText(profile.referralCode);
      setShowCopyToast(true);
      setTimeout(() => setShowCopyToast(false), 2000);
    }
  };

  const shareWhatsApp = (referralLink, projectName) => {
    const message = `👋 شارك رابط الإحالة الخاص بك:\n\nاسم المشروع: ${projectName}\nرابط الإحالة: ${referralLink}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    if (!userActivities || !currentUser) {
      setActivitiesLoading(false);
      return;
    }

    setActivitiesLoading(true);
    const unsubscribes = [];

    userActivities.forEach((activity) => {
      const { id, referralCode, slug } = activity;
      const activitySlug = (slug || "").trim();
      const activityKey = `${id}_visits_total`;

      if (activitySlug === "visit_open") {
        const linksQ = query(
          collection(db, "referralLinks"),
          where("memberId", "==", currentUser.uid),
        );
        const unsub = onSnapshot(linksQ, (snap) => {
          let total = 0;
          snap.forEach((d) => {
            const v = d.data().visitsCount;
            if (typeof v === "number") total += v;
          });
          updateCount(activityKey, total);
        });
        unsubscribes.push(unsub);
      } else if (activitySlug === "complaint_approved") {
        const uaQ = query(
          collection(db, "users_activities"),
          where("userId", "==", currentUser.uid),
          where("activityId", "==", id),
          limit(1),
        );
        const unsub = onSnapshot(uaQ, (snap) => {
          if (!snap.empty) {
            const row = snap.docs[0].data();
            updateCount(`${id}_complaints`, row.complaintsApprovedCount || 0);
          } else {
            updateCount(`${id}_complaints`, 0);
          }
        });
        unsubscribes.push(unsub);
      } else if (activitySlug === "store_suggestion_approved") {
        const uaQ = query(
          collection(db, "users_activities"),
          where("userId", "==", currentUser.uid),
          where("activityId", "==", id),
          limit(1),
        );
        const unsub = onSnapshot(uaQ, (snap) => {
          if (!snap.empty) {
            const row = snap.docs[0].data();
            updateCount(
              `${id}_suggestions`,
              row.storeSuggestionsApprovedCount || 0,
            );
          } else {
            updateCount(`${id}_suggestions`, 0);
          }
        });
        unsubscribes.push(unsub);
      } else if (activitySlug === "referral_registration") {
        const uaQ = query(
          collection(db, "users_activities"),
          where("userId", "==", currentUser.uid),
          where("activityId", "==", id),
          limit(1),
        );
        const unsub = onSnapshot(uaQ, (snap) => {
          if (!snap.empty) {
            const row = snap.docs[0].data();
            updateCount(
              `${id}_referrals`,
              row.referralRegistrationCount || 0,
            );
          } else {
            updateCount(`${id}_referrals`, 0);
          }
        });
        unsubscribes.push(unsub);
      }
    });

    setTimeout(() => setActivitiesLoading(false), 500);
    return () => unsubscribes.forEach((u) => u());
  }, [userActivities, currentUser]);

  if (loading) {
    return (
      <div className="profile-page">
        <div className="profile-container">
          <h1>الملف الشخصي</h1>
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <div className="loading-text">جاري تحميل بياناتك...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="profile-page">
        <div className="profile-container">
          <h1>الملف الشخصي</h1>
          <div className="error-container">
            <div className="error-icon">⚠️</div>
            <div className="error-message">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="profile-page">
        <div className="profile-container">
          <h1>الملف الشخصي</h1>
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <div className="loading-text">جاري تحميل البيانات...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-container">
        <h1>الملف الشخصي</h1>

        {isEditing ? (
          <div className="profile-form-container">
            <form onSubmit={handleSubmit} className="profile-form">
              <div className="form-group">
                <label>الاسم</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="أدخل اسمك الكامل"
                />
              </div>

              <div className="form-group">
                <label>رقم الهاتف</label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  placeholder="أدخل رقم هاتفك"
                  dir="ltr"
                />
              </div>

              <div className="form-group">
                <label>المدينة</label>
                <select
                  name="city"
                  value={formData.city}
                  onChange={handleCityChange}
                >
                  <option value="">اختر المدينة</option>
                  {citiesList.map((city) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>الحي</label>
                <select
                  name="district"
                  value={formData.district}
                  onChange={handleChange}
                  disabled={!formData.city}
                >
                  <option value="">
                    {formData.city ? "اختر الحي" : "اختر المدينة أولاً"}
                  </option>
                  {districts.map((district) => (
                    <option key={district} value={district}>
                      {district}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-buttons">
                <button type="submit" className="primary-button">
                  حفظ التغييرات
                </button>
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="secondary-button"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        ) : (
          <div className="profile-details-container">
            <div className="profile-card">
              <div className="profile-header">
                <div className="profile-info">
                  <h2>{profile.name || "المستخدم"}</h2>
                  <span className="member-level">
                    {profile.membershipLevel || "عضو جديد"}
                  </span>
                </div>
                <button onClick={handleEdit} className="edit-button">
                  تعديل البيانات
                </button>
              </div>

              <div className="profile-stats">
                <div className="stat-card">
                  <div className="stat-icon">🏆</div>
                  <span className="stat-label">النقاط المكتسبة</span>
                  <span className="stat-value">{profile.points || 0} / 5000</span>
                </div>

                {/* بطاقة رابط الإحالة */}
                <div className="stat-card stat-card-link">
                  <div className="stat-icon">🔗</div>
                  <div className="stat-content">
                    <span className="stat-label">رابط العضوية</span>

                    {referralLinks?.[0]?.referralLink ? (
                      <a
                        className="ref-link"
                        href={referralLinks[0].referralLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={referralLinks[0].referralLink}
                        dir="ltr"
                      >
                        {referralLinks[0].referralLink}
                      </a>
                    ) : (
                      <span className="stat-value">لا يوجد</span>
                    )}
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon">📊</div>
                  <span className="stat-label">الأنشطة النشطة</span>
                  <span className="stat-value">
                    {userActivities
                      ? userActivities.filter(
                          (a) => a.status !== "unsubscribed",
                        ).length
                      : 0}
                  </span>
                </div>
              </div>

              <div className="profile-section">
                <h3>معلومات الاتصال</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <div className="info-icon">📧</div>
                    <div className="info-content">
                      <span className="info-label">البريد الإلكتروني</span>
                      <span className="info-value">
                        {profile.email || "غير محدد"}
                      </span>
                    </div>
                  </div>
                  <div className="info-item">
                    <div className="info-icon">📱</div>
                    <div className="info-content">
                      <span className="info-label">رقم الهاتف</span>
                      <span className="info-value" dir="ltr">
                        {profile.phoneNumber || "غير محدد"}
                      </span>
                    </div>
                  </div>
                  <div className="info-item">
                    <div className="info-icon">📍</div>
                    <div className="info-content">
                      <span className="info-label">العنوان</span>
                      <span className="info-value">
                        {profile.district || profile.city
                          ? `${profile.district || "غير محدد"}, ${profile.city || "غير محدد"}`
                          : "غير محدد"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="activities-section">
              <h3>أنشطتي</h3>
              {activitiesLoading ? (
                <div className="activities-loading">
                  <div className="loading-spinner"></div>
                  <div className="loading-text">جاري تحميل الأنشطة...</div>
                </div>
              ) : userActivities && userActivities.length > 0 ? (
                <div className="table-responsive">
                  <table className="activities-table">
                    <thead>
                      <tr>
                        <th>اسم النشاط</th>
                        <th>عدد المرات المكتملة</th>
                        <th>النقاط المكتسبة</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userActivities.map((activity) => {
                        const isVisit =
                          (activity.slug || "").trim() === "visit_open";
                        const isComplaint =
                          (activity.slug || "").trim() === "complaint_approved";
                        const isSuggestion =
                          (activity.slug || "").trim() ===
                          "store_suggestion_approved";
                        const isReferral =
                          (activity.slug || "").trim() ===
                          "referral_registration";

                        const activityKey = isVisit
                          ? `${activity.id}_visits_total`
                          : isComplaint
                            ? `${activity.id}_complaints`
                            : isSuggestion
                              ? `${activity.id}_suggestions`
                              : isReferral
                                ? `${activity.id}_referrals`
                                : `${activity.id}`;

                        const dynamicValue = dynamicCounts[activityKey] || 0;
                        const earnedPoints =
                          (activity.points || 0) * dynamicValue;

                        {
                          showCopyToast && (
                            <div className="copy-toast">
                              ✓ تم نسخ كود الإحالة بنجاح
                            </div>
                          );
                        }

                        return (
                          <tr key={activity.id}>
                            <td>{activity.name || "غير متوفر"}</td>
                            <td>{dynamicValue}</td>
                            <td>{earnedPoints}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="activities-empty">
                  <div className="activities-empty-icon">📋</div>
                  <div className="activities-empty-text">جاري التحميل ...</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProfilePage;
